import { CreateParkingRangerClientZoneAssignmentDTO, EditParkingRangerClientZoneAssignmentDTO, IPagedResultDTOOfParkingRangerClientZoneAssignmentDTO, IParkingRangerClientZoneAssignmentDTO, ParkingRangerClientZoneAssignmentClient, ParkingRangerClientZoneAssignmentUserInfoDTO } from '../../../base/models/models.swagger.generated';
import { Injectable } from '@angular/core';
import { catchError, forkJoin, Observable, tap, throwError } from 'rxjs';
import { FilterDTO, SortDirection } from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { ShowInactiveService } from 'src/app/base/shared/grid-base/show-inactive.service';
import { IAdaptorService } from 'src/app/base/shared/syncfusion/adaptor-service.interface';
import { ZonePathUtil } from 'src/app/base/shared/utils/zone-path.util';
import { ErrorType } from 'src/app/core/routing/error-types';

@Injectable()
export class ParkingRangerAssignmentAdministrationService extends ShowInactiveService implements IAdaptorService {
  public readonly extendedZonePathFieldName = 'zone.' + ZonePathUtil.extendedZonePathFieldName;

  constructor(private toastService: ToastService, private prAssignmentClient: ParkingRangerClientZoneAssignmentClient) {
    super();
  }

  public getData(skip: number, take: number, filters: FilterDTO[], sortBy: string, sortDirection: SortDirection): Observable<IPagedResultDTOOfParkingRangerClientZoneAssignmentDTO> {

    return this.prAssignmentClient.searchParkingRangerClientZoneAssignments(filters, skip, take, sortBy, sortDirection)
      .pipe(
        tap((result: IPagedResultDTOOfParkingRangerClientZoneAssignmentDTO) => {
          result.items.forEach((item: IParkingRangerClientZoneAssignmentDTO) => {
            ZonePathUtil.extendByZonePath(item.zone);
          });
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public updateRecord(data: any): Observable<any> {
    throw new Error('Method not implemented.');
  }

  public addRecord(data: any): Observable<any> {
    throw new Error('Method not implemented.');
  }

  public deleteRecord(id: number): Observable<any> {
    return this.prAssignmentClient.deleteParkingRangerClientZoneAssignment(id)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.parking-ranger-assignment-administration.messages.delete-title',
            'i18n.domain.parking-ranger-assignment-administration.messages.delete-success-message'
          );
        }),
        catchError((error: any) => {
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
          }
          return throwError(() => error);
        })
      );
  }

  public bulkDeleteRecords(userId: string, clientId: number): Observable<any> {
    return this.prAssignmentClient.bulkDeleteParkingRangerClientZoneAssignment(userId, clientId).pipe(
      tap(() => {
          this.toastService.showSuccess(
            'i18n.domain.parking-ranger-assignment-administration.messages.bulk-delete-title',
            'i18n.domain.parking-ranger-assignment-administration.messages.bulk-delete-success-message'
          );
        }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public createAssignments(
    userId: string,
    clientId: number,
    zoneIds: number[] | undefined,
    isRegistrationCreationEnabled: boolean,
    isSessionCreationEnabled: boolean,
    isCurrentPositionEnabled: boolean,
    isPositionTrackingEnabled: boolean,
    newSessionCreationAfterHours: number,
    previewConsideredHours: number
  ): Observable<any> {
    const createCalls: Observable<void>[] = [];

    if (!zoneIds || zoneIds.length === 0) {
      createCalls.push(
        this.prAssignmentClient.createParkingRangerClientZoneAssignments(
          new CreateParkingRangerClientZoneAssignmentDTO({
            userId, clientId, zoneId: undefined,
            isRegistrationCreationEnabled, isSessionCreationEnabled,
            isCurrentPositionEnabled, isPositionTrackingEnabled,
            newSessionCreationAfterHours, previewConsideredHours
          })
        )
      );
    } else {
      for (const zoneId of zoneIds) {
        createCalls.push(
          this.prAssignmentClient.createParkingRangerClientZoneAssignments(
            new CreateParkingRangerClientZoneAssignmentDTO({
              userId, clientId, zoneId,
              isRegistrationCreationEnabled, isSessionCreationEnabled,
              isCurrentPositionEnabled, isPositionTrackingEnabled,
              newSessionCreationAfterHours, previewConsideredHours
            })
          )
        );
      }
    }

    return forkJoin(createCalls).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.parking-ranger-assignment-administration.messages.add-title',
          'i18n.domain.parking-ranger-assignment-administration.messages.add-success-message'
        );
      }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public editAssignment(
    id: number,
    isRegistrationCreationEnabled: boolean,
    isSessionCreationEnabled: boolean,
    isCurrentPositionEnabled: boolean,
    isPositionTrackingEnabled: boolean,
    newSessionCreationAfterHours: number,
    previewConsideredHours: number
  ): Observable<any> {
    return this.prAssignmentClient.editParkingRangerClientZoneAssignments(
      new EditParkingRangerClientZoneAssignmentDTO({
        id,
        isRegistrationCreationEnabled,
        isSessionCreationEnabled,
        isCurrentPositionEnabled,
        isPositionTrackingEnabled,
        newSessionCreationAfterHours,
        previewConsideredHours
      })
    ).pipe(
      tap(() => {
        this.toastService.showSuccess(
          'i18n.domain.parking-ranger-assignment-administration.messages.edit-title',
          'i18n.domain.parking-ranger-assignment-administration.messages.edit-success-message'
        );
      }),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }

  public searchParkingRangers(searchText: string): Observable<ParkingRangerClientZoneAssignmentUserInfoDTO[]> {
    return this.prAssignmentClient.searchParkingRangers(searchText, 0, 10).pipe(
      tap(

      ),
      catchError((error: any) => {
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message', null, ErrorType.serverError);
        }
        return throwError(() => error);
      })
    );
  }
}
